import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@components/ui';
import Tabs from '@components/ui/Tabs/Tabs';
import DataTable from '@components/shared/DataTable/DataTable';
import type { DataTableColumn, SortState } from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import { getUsers } from '@/services/userService';
import { useAuthStore } from '@/stores/authStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import { useTranslation } from '@/hooks/useTranslation';
import { buildSortString, toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import PlusIcon from '@assets/icons/PlusIcon';
import UsersEmptyIcon from '@assets/icons/UsersEmptyIcon';
import type { PlatformUserDto, PlatformUserStatus, PlatformUserType } from '@/types/user';
import type { FilterFieldConfig } from '@/types/filters';
import type { TabItem } from '@components/ui/Tabs/Tabs';
import type { TableSearchParams } from '@utils/tabHelpers';
import ViewUserModal from './ViewUserModal';
import CreateUserModal from './CreateUserModal';
import EditUserModal from './EditUserModal';
import AssignRolesModal from './AssignRolesModal';
import styles from './UserListPage.module.scss';

// ============================================
// TYPES
// ============================================

interface UserListPageProps {
  routeTo: string;
  searchParams: TableSearchParams;
}

// ============================================
// HELPERS
// ============================================

function StatusBadge({ status }: { status: PlatformUserStatus }) {
  const className = status === 'ACTIVE' ? styles.badgeActive : styles.badgeInactive;
  return (
    <span className={`${styles.badge} ${className}`}>
      {toTitleCase(status)}
    </span>
  );
}

function UserTypeBadge({ userType }: { userType: PlatformUserType }) {
  const badgeClass =
    userType === 'ADMIN'
      ? styles.badgeBrand
      : userType === 'EMPLOYEE'
        ? styles.badgeActive
        : styles.badgeInactive;
  return (
    <span className={`${styles.badge} ${badgeClass}`}>
      {toTitleCase(userType)}
    </span>
  );
}


// ============================================
// COMPONENT
// ============================================

function UserListPage({ routeTo, searchParams }: UserListPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam, tab } = searchParams;
  const page = urlPage ?? 0;
  const size = urlSize ?? 10;
  const activeTab = tab ?? 'ALL';

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  // -- Tabs --
  const tabs = useMemo<TabItem[]>(
    () => [
      { id: 'ALL', label: t('K_TAB_ALL_USERS', 'All') },
      { id: 'ACTIVE', label: t('K_TAB_ACTIVE', 'Active') },
      { id: 'INACTIVE', label: t('K_TAB_INACTIVE', 'Inactive') },
    ],
    [t],
  );

  const handleTabChange = useCallback(
    (tabId: string) => {
      void navigate({
        to: routeTo,
        search: { page: 0, size, tab: tabId },
        replace: true,
      });
    },
    [navigate, routeTo, size],
  );

  // -- Filter fields (ordered to match columns: Name, Email, Type, Roles, Status, Created) --
  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'userType',
        label: t('K_FILTER_USER_TYPE', 'User Type'),
        type: 'select',
        options: [
          { label: t('K_OPTION_STANDARD', 'Standard'), value: 'STANDARD' },
          { label: t('K_OPTION_ADMIN', 'Admin'), value: 'ADMIN' },
          { label: t('K_OPTION_EMPLOYEE', 'Employee'), value: 'EMPLOYEE' },
        ],
      },
      {
        fieldKey: 'status',
        label: t('K_FILTER_STATUS', 'Status'),
        type: 'select',
        options: [
          { label: toTitleCase(t('K_STATUS_ACTIVE', 'Active')), value: 'ACTIVE' },
          { label: toTitleCase(t('K_STATUS_INACTIVE', 'Inactive')), value: 'INACTIVE' },
        ],
      },
    ],
    [t],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo,
    filtersParam,
    fields: filterFields,
  });

  // -- Modals --
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewItem, setViewItem] = useState<PlatformUserDto | null>(null);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [assignRolesUserId, setAssignRolesUserId] = useState<string | null>(null);
  const [auditUser, setAuditUser] = useState<{ id: string; name: string } | null>(null);

  const handleCreate = useCallback(() => setIsCreateOpen(true), []);
  const handleCloseCreate = useCallback(() => setIsCreateOpen(false), []);
  const handleCloseView = useCallback(() => setViewItem(null), []);
  const handleCloseEdit = useCallback(() => setEditUserId(null), []);
  const handleCloseAssignRoles = useCallback(() => setAssignRolesUserId(null), []);

  const handleEditFromView = useCallback(() => {
    if (viewItem) {
      setEditUserId(viewItem.id);
      setViewItem(null);
    }
  }, [viewItem]);

  const handleAuditFromView = useCallback(() => {
    if (viewItem) {
      setAuditUser({
        id: viewItem.id,
        name: `${viewItem.firstName} ${viewItem.lastName ?? ''}`.trim(),
      });
      setViewItem(null);
    }
  }, [viewItem]);

  // -- Columns --
  const columns = useMemo<DataTableColumn<PlatformUserDto>[]>(
    () => [
      {
        id: 'name',
        header: t('K_COLUMN_NAME', 'Name'),
        sortable: true,
        sortKey: 'firstName',
        width: '200px',
        cell: (item) => {
          const displayName = `${item.firstName} ${item.lastName}`.trim();
          return (
            <span className={styles.cellPrimary} title={displayName}>
              {displayName}
            </span>
          );
        },
      },
      {
        id: 'email',
        header: t('K_COLUMN_EMAIL', 'Email'),
        sortable: true,
        sortKey: 'email',
        cell: (item) => (
          <span className={styles.cellSecondary}>{item.email}</span>
        ),
      },
      {
        id: 'userType',
        header: t('K_COLUMN_USER_TYPE', 'Type'),
        cell: (item) => <UserTypeBadge userType={item.userType} />,
      },
      {
        id: 'roles',
        header: t('K_COLUMN_ROLES', 'Roles'),
        cell: (item) =>
          item.roles.length > 0 ? (
            <div className={styles.roleBadges}>
              {item.roles.map((role) => (
                <span key={role.id} className={styles.roleBadge}>
                  {role.name}
                </span>
              ))}
            </div>
          ) : (
            <span className={styles.cellSecondary}>-</span>
          ),
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (item) => <StatusBadge status={item.status} />,
      },
      {
        id: 'effectiveFrom',
        header: t('K_COLUMN_CREATED', 'Created'),
        sortable: true,
        sortKey: 'effectiveFrom',
        cell: (item) => (
          <span className={styles.cellSecondary}>
            {item.effectiveFrom
              ? new Date(item.effectiveFrom).toLocaleDateString()
              : '-'}
          </span>
        ),
      },
    ],
    [t],
  );

  const rowActions = useCallback((item: PlatformUserDto) => {
    const actions: ProfileAction[] = [
      { id: 'view', label: t('K_VIEW', 'View'), onClick: () => setViewItem(item) },
    ];
    if (hasPermission('USERS_UPDATE')) {
      actions.push({ id: 'edit', label: t('K_EDIT', 'Edit'), onClick: () => setEditUserId(item.id) });
      actions.push({ id: 'assign-roles', label: t('K_ASSIGN_ROLES', 'Assign Roles'), onClick: () => setAssignRolesUserId(item.id) });
    }
    if (hasPermission('AUDIT_READ')) {
      actions.push({
        id: 'audit',
        label: t('K_AUDIT_TITLE', 'Audit trail'),
        onClick: () =>
          setAuditUser({
            id: item.id,
            name: `${item.firstName} ${item.lastName ?? ''}`.trim(),
          }),
      });
    }
    return <ProfileActionsMenu actions={actions} />;
  }, [t, hasPermission]);

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  // -- Build API filter payload --
  const apiPayload = useMemo(() => {
    const payload: Record<string, unknown> = {};
    const apiFilters: Record<string, unknown> = {};

    // Tab-level status filter (not from filter panel)
    if (activeTab !== 'ALL') {
      apiFilters.status = activeTab;
    }

    // Merge filter panel values
    if (filterPayload?.filters) {
      Object.assign(apiFilters, filterPayload.filters);
    }

    if (Object.keys(apiFilters).length > 0) {
      payload.filters = apiFilters;
    }
    if (filterPayload?.search) {
      payload.search = filterPayload.search;
    }

    return payload;
  }, [activeTab, filterPayload]);

  // -- Data fetching --
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['users', { page, size, sort: sortString, tab: activeTab, filterPayload }],
    queryFn: () => getUsers({ page, size, sort: sortString }, apiPayload),
  });

  const items = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);
  const getRowKey = useCallback((item: PlatformUserDto) => item.id, []);
  const handleRowClick = useCallback((item: PlatformUserDto) => setViewItem(item), []);

  // -- Navigation --
  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: routeTo,
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate, routeTo],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: routeTo,
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate, routeTo],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: routeTo,
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate, routeTo],
  );

  // -- Empty state --
  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <UsersEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>{t('K_EMPTY_NO_USERS', 'No users yet')}</h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_CREATE_FIRST_USER', 'Get started by inviting your first user.')}
          </p>
          {hasPermission('USERS_CREATE') && (
            <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
              {t('K_BUTTON_CREATE_USER', 'Create User')}
            </Button>
          )}
        </div>
      ),
    [listForbidden, handleCreate, t, hasPermission],
  );

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_USERS', 'Users')}</h1>
          <p className={styles.subtitle}>
            {t('K_PAGE_SUBTITLE_USERS', 'Manage platform users and their access')}
          </p>
        </div>
        {hasPermission('USERS_CREATE') && (
          <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
            {t('K_BUTTON_CREATE_USER', 'Create User')}
          </Button>
        )}
      </div>

      {/* Tabs */}
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
      />

      {/* Applied Filters */}
      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={items}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        fetching={isFetching && !isLoading}
        loadingText={t('K_LOADING_USERS', 'Loading users...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        aria-label="Users list"
      />

      {/* Filter Panel */}
      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      {/* View Modal */}
      <ViewUserModal
        isOpen={viewItem !== null}
        onClose={handleCloseView}
        data={viewItem}
        onEdit={handleEditFromView}
        onAudit={handleAuditFromView}
      />

      {/* Create Modal */}
      <CreateUserModal
        isOpen={isCreateOpen}
        onClose={handleCloseCreate}
      />

      {/* Edit Modal */}
      <EditUserModal
        isOpen={editUserId !== null}
        onClose={handleCloseEdit}
        userId={editUserId}
      />

      {/* Assign Roles Modal */}
      <AssignRolesModal
        isOpen={assignRolesUserId !== null}
        onClose={handleCloseAssignRoles}
        userId={assignRolesUserId}
      />

      {/* Audit Trail Modal */}
      <AuditTrailModal
        open={auditUser !== null}
        onClose={() => setAuditUser(null)}
        filter={{ resourceType: AUDIT_RESOURCE.USER, resourceId: auditUser?.id ?? '' }}
        subject={auditUser?.name}
      />
    </div>
  );
}

export default UserListPage;
